import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Form, Label, TextArea } from '@shared/components';
import {
    COMPLETION_NOTE_MAX_LENGTH,
    completeCancellationSchema,
    type CompleteCancellationFormInput,
    type CompleteCancellationValues,
} from '../../../schemas/completeCancellation.schema';
import { COMPLETION_COPY, COMPLETION_OPTION_FOR_REASON } from '../../../utils/cancellationCopy';
import type { CompletableReason, CompletionOption } from '../../../types/cancellation.types';

interface Props {
    reason: CompletableReason;
    pending: boolean;
    serverError: string | null;
    onSubmit: (option: CompletionOption, note: string) => void;
}

export function CompleteCancellationPanel({ reason, pending, serverError, onSubmit }: Props) {
    const headingId = useId();
    const option = COMPLETION_OPTION_FOR_REASON[reason];
    const copy = COMPLETION_COPY[option];

    return (
        <Card ariaLabelledBy={headingId}>
            <CardHeader>
                <CardTitle as="h3" id={headingId}>
                    Complete cancellation
                </CardTitle>
                <p className="mt-1 text-sm text-mp-muted">Only MotorPlan can complete a cancellation that is waiting.</p>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    <section className="rounded-lg border border-mp-border bg-mp-background p-4">
                        <h4 className="text-sm font-semibold text-mp-foreground">{copy.title}</h4>
                        <p className="mt-1 text-sm text-mp-muted">{copy.effect}</p>
                    </section>
                    {serverError && <Alert variant="error">{serverError}</Alert>}
                    <CompleteCancellationForm
                        pending={pending}
                        submitLabel={copy.submitLabel}
                        pendingLabel={copy.pendingLabel}
                        onSubmit={(values) => onSubmit(option, values.note)}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

interface FormProps {
    pending: boolean;
    submitLabel: string;
    pendingLabel: string;
    onSubmit: (values: CompleteCancellationValues) => void;
}

function CompleteCancellationForm({ pending, submitLabel, pendingLabel, onSubmit }: FormProps) {
    const noteId = useId();
    const {
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<CompleteCancellationFormInput, unknown, CompleteCancellationValues>({
        resolver: zodResolver(completeCancellationSchema),
        mode: 'onBlur',
        defaultValues: { note: '' },
        shouldFocusError: false,
    });
    const note = watch('note');

    return (
        <Form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
                <Label htmlFor={noteId} required>
                    Note
                </Label>
                <TextArea
                    id={noteId}
                    rows={4}
                    placeholder="Why is this being completed this way?"
                    value={note}
                    onChange={(value) => setValue('note', value, { shouldValidate: !!errors.note })}
                    error={!!errors.note}
                    disabled={pending}
                    hint={errors.note?.message}
                    hintId={`${noteId}-error`}
                    required
                />
                <p className="mt-1 text-right text-xs text-mp-muted" aria-live="polite">
                    {note.length} / {COMPLETION_NOTE_MAX_LENGTH}
                </p>
            </div>
            <div className="flex sm:justify-end">
                <Button type="submit" variant="danger" appearance="solid" disabled={pending} className="w-full sm:w-auto">
                    {pending ? pendingLabel : submitLabel}
                </Button>
            </div>
        </Form>
    );
}
